import { Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

interface PageMeta {
  title: string;
  description: string;
  keywords?: string;
}

@Injectable({ providedIn: 'root' })
export class SeoService {

  private pageMetas: Record<string, PageMeta> = {

    '/': {
      title: 'Referio — L\'annuaire qui rend les professionnels visibles en un clic',
      description: 'Trouvez rapidement le bon professionnel près de chez vous ou référencez votre activité gratuitement. Referio, l\'annuaire nouvelle génération en Belgique.',
      keywords: 'annuaire professionnel, trouver artisan, référencer entreprise, professionnel Belgique, annuaire en ligne'
    },

    '/carte': {
      title: 'Carte des professionnels — Referio',
      description: 'Recherchez des professionnels autour de vous sur la carte interactive Referio. Filtrez par catégorie, note et localisation pour trouver le bon expert près de chez vous.',
      keywords: 'carte professionnels, professionnel près de moi, artisan local, géolocalisation, Belgique'
    },

    '/favoris': {
      title: 'Mes favoris — Referio',
      description: 'Retrouvez tous les professionnels que vous avez sauvegardés sur Referio et gérez facilement vos prestataires préférés.',
    },

    '/tableau-de-bord': {
      title: 'Tableau de bord — Referio',
      description: 'Gérez votre profil professionnel, suivez vos statistiques de visibilité et mettez à jour vos informations depuis votre espace Referio.',
    },

    '/mentions-legales': {
      title: 'Mentions légales — Referio',
      description: 'Consultez les mentions légales du site Referio : informations sur l\'éditeur, l\'hébergement, la propriété intellectuelle et les conditions d\'utilisation.',
    },

    '/politique-confidentialite': {
      title: 'Politique de confidentialité — Referio',
      description: 'Découvrez comment Referio collecte, utilise et protège vos données personnelles, conformément au RGPD et à la loi belge du 30 juillet 2018.',
    },

    '/contact': {
      title: 'Contact & Support — Referio',
      description: 'Vous avez une question ou besoin d\'aide ? Contactez l\'équipe Referio via notre formulaire ou par email. Réponse garantie sous 24 à 48h ouvrables.',
      keywords: 'contact Referio, support, aide, question, assistance'
    },

  };

  constructor(private meta: Meta, private titleService: Title, private router: Router) {}

  /**
   * Initialise la mise à jour automatique des metas à chaque changement de route.
   * À appeler une seule fois dans AppComponent.
   */
  init(): void {
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe((e: NavigationEnd) => {
        this.updateMeta(e.urlAfterRedirects.split('?')[0]);
      });
  }

  /**
   * Met à jour manuellement les metas pour une route donnée.
   * Utile dans les composants pour des pages dynamiques (fiche pro, etc.)
   */
  updateMeta(path: string, overrides?: Partial<PageMeta>): void {
    const page = { ...this.pageMetas[path], ...overrides };
    if (!page) return;

    // Title
    this.titleService.setTitle(page.title);

    // Standard meta
    this.meta.updateTag({ name: 'description', content: page.description });
    if (page.keywords) {
      this.meta.updateTag({ name: 'keywords', content: page.keywords });
    }

    // Open Graph
    this.meta.updateTag({ property: 'og:title', content: page.title });
    this.meta.updateTag({ property: 'og:description', content: page.description });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:site_name', content: 'Referio' });
    this.meta.updateTag({ property: 'og:url', content: `https://www.referio.be${path}` });
    this.meta.updateTag({ property: 'og:image', content: 'https://www.referio.be/assets/og-image.jpg' });

    // Twitter Card
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: page.title });
    this.meta.updateTag({ name: 'twitter:description', content: page.description });
    this.meta.updateTag({ name: 'twitter:image', content: 'https://www.referio.be/assets/og-image.jpg' });

    // Canonical
    this.updateCanonical(`https://www.referio.be${path}`);
  }

  private updateCanonical(url: string): void {
    let link: HTMLLinkElement = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }
}